import type { GameRuntime } from '../game/runtime'
import type { RacePhase, RacerRecordStatus, SimulationState } from '../game/types'
import { getVirtualPlayerInput } from '../game/virtual-driver'

const FRAME_CHUNK = 30
const WALL_DEADLINE_MS = 20_000

export type BrowserMilestone = 'racing' | 'lap-2' | 'final-lap' | 'finish-countdown' | 'finished'

export interface BrowserBridgeSnapshot {
  phase: RacePhase
  elapsed: number
  lap: number
  checkpoint: number
  place: number | null
  finishCountdown: number | null
  recordStatus: RacerRecordStatus
  tick: number
  time: number
}

export interface BrowserBridge {
  snapshot(): BrowserBridgeSnapshot
  advanceTo(milestone: BrowserMilestone): BrowserBridgeSnapshot
}

export interface BrowserBridgeOptions {
  now?: () => number
}

type BridgeRuntime = Pick<GameRuntime, 'getState' | 'isPaused' | 'runVirtualFrames'>

const MAX_FRAMES: Record<BrowserMilestone, number> = {
  racing: 600,
  'lap-2': 6_000,
  'final-lap': 7_500,
  'finish-countdown': 9_000,
  finished: 9_000,
}

const PHASE_ORDER: Record<RacePhase, number> = {
  countdown: 0,
  racing: 1,
  'finish-countdown': 2,
  finished: 3,
}

const MILESTONE_REACHED: Record<BrowserMilestone, (snapshot: BrowserBridgeSnapshot) => boolean> = {
  racing: (snapshot) => PHASE_ORDER[snapshot.phase] >= PHASE_ORDER.racing,
  'lap-2': (snapshot) => snapshot.lap >= 1,
  'final-lap': (snapshot) => snapshot.lap >= 2,
  'finish-countdown': (snapshot) => PHASE_ORDER[snapshot.phase] >= PHASE_ORDER['finish-countdown'],
  finished: (snapshot) => snapshot.phase === 'finished',
}

export function isE2EMode(search: string): boolean {
  const values = new URLSearchParams(search).getAll('e2e')
  return values.length === 1 && values[0] === '1'
}

function snapshotRuntime(state: SimulationState): BrowserBridgeSnapshot {
  const playerId = state.race.playerId
  const playerProgress = state.race.progress[playerId]
  const rankingIndex = state.ranking.indexOf(playerId)
  return {
    phase: state.race.phase,
    elapsed: state.race.elapsed,
    lap: playerProgress?.lap ?? 0,
    checkpoint: playerProgress?.lastCheckpoint ?? 0,
    place: rankingIndex >= 0 ? rankingIndex + 1 : null,
    finishCountdown: state.race.finishCountdown,
    recordStatus: playerProgress?.recordStatus ?? 'unrecorded',
    tick: state.tick,
    time: state.time,
  }
}

function unableToReach(
  milestone: BrowserMilestone,
  reason: string,
  advancedFrames: number,
  elapsedWallMs: number,
  snapshot: BrowserBridgeSnapshot,
): Error {
  return new Error(
    `Unable to reach ${milestone}: ${reason}; advanced frames ${advancedFrames}; elapsed wall ms ${elapsedWallMs}; snapshot ${JSON.stringify(snapshot)}`,
  )
}

export function createBrowserBridge(runtime: BridgeRuntime, options: BrowserBridgeOptions = {}): BrowserBridge {
  const snapshot = () => snapshotRuntime(runtime.getState())
  const now = options.now ?? (() => performance.now())

  return {
    snapshot,
    advanceTo(milestone) {
      const startedAt = now()
      const reached = MILESTONE_REACHED[milestone]
      let current = snapshot()
      let advancedFrames = 0
      let elapsedWallMs = 0
      if (runtime.isPaused()) {
        throw unableToReach(milestone, 'runtime paused', advancedFrames, elapsedWallMs, current)
      }
      if (reached(current)) return current
      if (current.phase === 'finished') {
        throw unableToReach(milestone, 'race finished before milestone', advancedFrames, elapsedWallMs, current)
      }

      while (advancedFrames < MAX_FRAMES[milestone]) {
        runtime.runVirtualFrames(FRAME_CHUNK, getVirtualPlayerInput)
        advancedFrames += FRAME_CHUNK
        current = snapshot()
        elapsedWallMs = Math.max(0, now() - startedAt)
        if (reached(current)) return current
        if (current.phase === 'finished') {
          throw unableToReach(milestone, 'race finished before milestone', advancedFrames, elapsedWallMs, current)
        }
        if (elapsedWallMs >= WALL_DEADLINE_MS) {
          throw unableToReach(milestone, 'wall-clock deadline exceeded', advancedFrames, elapsedWallMs, current)
        }
      }

      throw unableToReach(milestone, 'frame limit exhausted', advancedFrames, elapsedWallMs, current)
    },
  }
}
